// SPDX-License-Identifier: LicenseRef-MMS-WIRE
// Copyright (c) 2016-2026 Metal Management Solutions (Pty) Ltd

import {inject} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot} from '@angular/router';
import {AppScope} from '../app_scope.service';
import {AccountService} from '../account.service';
import {TimezoneSelectorService} from "../timezone-selector.service";
import {firstValueFrom, of} from "rxjs";
import {catchError, defaultIfEmpty, map, take, timeout} from "rxjs/operators";

// How long to wait for AccountService's /api/ActiveAccount round-trip before giving
// up and using the login-time cached default. The /view route only waits for user
// context, not for that request, so on a fresh load the active account is still in
// flight when this guard resumes. `activeAccountChanged` is a ReplaySubject that
// NEVER emits when there is no active account or the request fails, so this must be
// bounded — an unbounded await would hang the root route on a blank screen.
const ACTIVE_ACCOUNT_WAIT_MS = 5000;

export const urlGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const appScope: AppScope = inject(AppScope);
    const router: Router = inject(Router);
    const accountService: AccountService = inject(AccountService);
    const timezoneService: TimezoneSelectorService = inject(TimezoneSelectorService);

    const userLoaded = await firstValueFrom(appScope.userContextLoaded);
    if (!userLoaded) {
        console.error("User context was not loaded for url guard.");
        return false;
    }

    // `current_user.default_dashboard_id` is resolved ONCE at login, for whichever
    // account was active then, and AccountService.setActiveAccount never refreshes
    // it (nor does page-view-form, which writes it from a per-account preference).
    // Relying on it sends the user to a page that may be deleted (→ /notfound), in
    // another account (→ the page guard silently switches the account back), or
    // undefined. Resolve the CURRENT account's default instead, and fall back to the
    // cached value ONLY when this account's default could not be determined at all —
    // see the `lookupSucceeded` note below for why the two cases must not share a
    // branch.
    const accountId: string | null = await firstValueFrom(
        accountService.activeAccountChanged.pipe(
            take(1),
            map(a => a?.account_id ?? null),
            timeout(ACTIVE_ACCOUNT_WAIT_MS),
            catchError(() => of(null)),
            // firstValueFrom REJECTS with EmptyError on a source that completes
            // without emitting, and catchError does not intercept that — it would
            // surface as a failed guard, not as the fallback below.
            defaultIfEmpty(null)
        )
    );


    // `ok` separates "this account HAS no default page" (a successful answer —
    // getDefaultUserSessionState emits undefined, it does not error) from "we could
    // not find out" (no active account, the lookup failed, or it completed without
    // emitting). Collapsing both to a falsy id, as this first did, sent the second
    // case and the first down the same fallback.
    let lookupSucceeded = false;
    let targetId: string | null = null;
    if (accountId) {
        const result = await firstValueFrom(
            accountService.getDefaultUserSessionState(accountId, appScope.current_user.id).pipe(
                take(1),
                map(id => ({ok: true, id: id || null})),
                catchError(() => of({ok: false, id: null})),
                // firstValueFrom REJECTS with EmptyError on a source that completes
                // without emitting, and catchError does not intercept that — it would
                // surface as a failed guard, not as the fallback below.
                defaultIfEmpty({ok: false, id: null})
            )
        );
        lookupSucceeded = result.ok;
        targetId = result.id;
    }
    // The login-time cached id is only sensible when we could not determine this
    // account's default. When the lookup SUCCEEDED and said there is none, any
    // non-null cached id necessarily belongs to a DIFFERENT account: auth.py resolves
    // it with the same precedence this lookup uses (the user's UserPreferences for the
    // active account, else the account's is_default page), so for the current account
    // it would have come back null too. Routing there would undo the user's account
    // switch — the very thing this guard exists to stop.
    if (!targetId && !lookupSucceeded) {
        targetId = appScope.current_user.default_dashboard_id || null;
    }

    if (targetId) {
        router.navigate(['view', 'page_view', targetId]);
    } else {
        router.navigate(['view', 'page_list_view']);
    }
    timezoneService.refresh();
    return true;
};
